import _ from 'lodash';
import type { PersistenceLevel } from '@/core/flux.service';
import { Store } from '@/core/flux.service';
import type { CapabilityEnum, CapabilityInheritanceOutputV1 } from '@/sdk/model/CapabilityInheritanceOutputV1';
import type { UserGroupOutputV1 } from '@/sdk/model/UserGroupOutputV1';
import type { UserOutputV1 } from '@/sdk/model/UserOutputV1';

interface AdminContactInfo {
  name: string;
  email: string;
}

interface AdministrationGroupDatasource {
  name?: string;
  datasourceId?: string;
  datasourceClass?: string;
}

export type AdministrationGroup = UserGroupOutputV1 & {
  datasource?: AdministrationGroupDatasource;
};

interface SetUserTableLoadingPayload {
  loading: boolean;
}

interface UpdateContactInfoPayload extends AdminContactInfo {}

/**
 * The administration store handles the known users.
 */
export class AdministrationStore extends Store {
  persistenceLevel: PersistenceLevel = 'NONE';
  static readonly storeName = 'sqAdministrationStore';

  initialize() {
    this.state = this.immutable({
      groups: [] as AdministrationGroup[],
      capabilitiesInEditor: [] as CapabilityInheritanceOutputV1[],
      groupUnsavedChanges: false,
      editorCapabilities: [],
      adminContactInfo: {
        name: '',
        email: '',
      } as AdminContactInfo,
    });
  }

  get groups(): AdministrationGroup[] {
    return this.state.get('groups');
  }

  get capabilitiesInEditor(): CapabilityInheritanceOutputV1[] {
    return this.state.get('capabilitiesInEditor');
  }

  get adminContactInfo(): AdminContactInfo {
    return this.state.get('adminContactInfo');
  }

  get tooManyRequests(): boolean {
    return this.state.get('tooManyRequests');
  }

  protected readonly handlers = {
    ADMINISTRATION_SET_USERS: (userList: UserOutputV1[]) => {
      this.state.set('users', userList);
    },

    ADMINISTRATION_SET_USER_TABLE_LOADING: ({ loading }: SetUserTableLoadingPayload) => {
      this.state.set('userTableLoading', loading);
    },

    /**
     * Clears the array of groups
     */
    ADMINISTRATION_CLEAR_GROUPS: () => {
      this.state.set('groups', []);
    },

    ADMINISTRATION_ADD_GROUP: (group: AdministrationGroup) => {
      this.state.push('groups', group);
    },

    /**
     * Updates properties for a group
     *
     * @param {Object} group - The group to be updated
     */
    ADMINISTRATION_UPDATE_GROUP: (group: Partial<AdministrationGroup> & Pick<AdministrationGroup, 'id'>) => {
      const index = _.findIndex(this.state.get('groups'), ['id', group.id]);
      if (index > -1) {
        this.state.merge(['groups', index], group);
      }
    },

    /**
     * Removes groups from the array of groups
     *
     * @param ids - The ids of the groups to be removed
     */
    ADMINISTRATION_REMOVE_GROUPS: (ids: Array<string | number>) => {
      _.forEach(ids, (id) => {
        const index = _.findIndex(this.state.get('groups'), ['id', id]);

        if (index >= 0) {
          this.state.splice('groups', [index, 1]);
        }
      });
    },

    ADMINISTRATION_CAPABILITIES_IN_EDITOR: (capabilitiesInEditor: CapabilityInheritanceOutputV1[]) => {
      this.state.set('capabilitiesInEditor', capabilitiesInEditor);
    },

    ADMINISTRATION_CAPABILITIES_TOGGLE_SELECTION: (capability: CapabilityEnum) => {
      const newSelections = _.some(this.capabilitiesInEditor, { capability })
        ? _.reject(this.capabilitiesInEditor, { capability })
        : _.concat(this.capabilitiesInEditor, { capability });
      this.state.set('capabilitiesInEditor', newSelections);
    },

    /**
     * Update the administrator's contact information in the store
     *
     * @param  {Object} adminContactInfo the contact information of the customer's Seeq administrator
     * @param  {String} adminContactInfo.name the name of the customer's Seeq administrator
     * @param  {String} adminContactInfo.email the email of the customer's Seeq administrator
     */
    ADMINISTRATION_UPDATE_CONTACT_INFO: (adminContactInfo: UpdateContactInfoPayload) => {
      this.state.set('adminContactInfo', adminContactInfo);
    },

    /**
     * Save boolean indicating if we have more than one
     * page of requests
     *
     * @param {Object} tooManyRequests - true if we have
     * more than one page of requests
     */
    ADMINISTRATION_SET_TOO_MANY_REQUESTS: (tooManyRequests: boolean) => {
      this.state.set('tooManyRequests', tooManyRequests);
    },
  };
}
